import { newUuidV7 } from '@ghp/domain'
import type { AssignedInquiry, FreeInspectionInput } from '@ghp/domain'

import type { CachedInquiry, DataDatabase } from './data-db'
import { createDraft } from './outbox'

/**
 * Ouvre un **contrôle libre** (#419, M41) : un contrôle fait sur place sans demande. Rien ne part
 * ici — hors réseau compris —, la fiche se saisit puis se transmet comme les autres.
 *
 * L'identifiant est **tiré par la tablette** (M7) : la fiche, les pièces et la file d'envoi vivent
 * sous lui, et le dépôt le présente au serveur, qui range la saisie sans demande. La ligne de
 * tournée est fabriquée ici, avec ce que l'agent a écrit ; `servedAt` absent dit que le serveur
 * ne l'a jamais servie.
 */
export async function createFreeInspection(
  db: DataDatabase,
  input: FreeInspectionInput,
  now: () => Date,
): Promise<string> {
  const id = newUuidV7()
  const row: CachedInquiry = {
    id,
    reference: null,
    caseReference: null,
    // Le contrôle est en cours dès qu'il existe : l'agent est devant la porte.
    status: 'in_progress',
    purpose: input.purpose,
    clientName: input.clientName,
    cadastralReference: null,
    buildingType: null,
    sewerNetworkType: null,
    // Le secteur se choisit au bureau, au rattachement, comme la commune (#580).
    lot: null,
    scheduledAt: null,
    appointmentDurationMinutes: null,
    appointmentImposedStep: null,
    comment: null,
    contact: null,
    owner: null,
    // L'adresse telle que l'agent l'a lue, d'un seul tenant : la commune se choisit au bureau.
    address: { street: input.address, postalCode: null, city: null, delegatedName: null },
    rejectionReason: null,
    tabletHiddenBlocks: [],
    followUpRank: null,
    followUpSource: null,
    officeSchematic: null,
    anomalyEffects: [],
    remindersLeft: 0,
    passageFollowUp: [],
    passageFollowUpDone: [],
    freeInspectionId: null,
    scheduledDay: null,
    freeInspection: { ...input, createdAt: now().toISOString() },
  }

  await db.inquiries.put(row)
  await createDraft(db, id)

  return id
}

/**
 * Le bureau a rattaché un contrôle libre de la tablette à une **demande existante**, puis la lui
 * renvoie : la tournée sert cette demande avec `freeInspectionId`, l'identifiant sous lequel la
 * tablette tient la fiche. La fiche, ses pièces et son historique d'envoi passent sous la demande,
 * et la ligne fabriquée s'efface — sans quoi la demande s'ouvrirait sur une fiche vide.
 *
 * ⚠️ **Rien ne se perd** : si la tablette tient déjà une fiche sous la demande, rien n'est
 * déplacé (M21) — le contrôle libre reste à sa place, et l'agent voit les deux.
 */
export async function adoptFreeInspections(
  db: DataDatabase,
  served: readonly AssignedInquiry[],
): Promise<void> {
  for (const inquiry of served) {
    const from = inquiry.freeInspectionId

    if (from == null || from === inquiry.id) continue

    await db.transaction('rw', [db.drafts, db.media, db.outbox, db.inquiries], async () => {
      const draft = await db.drafts.get(from)

      if (draft === undefined || (await db.drafts.get(inquiry.id)) !== undefined) return

      await db.drafts.put({ ...draft, inquiryId: inquiry.id })
      await db.drafts.delete(from)
      await db.media.where('inquiryId').equals(from).modify({ inquiryId: inquiry.id })
      await db.outbox.where('inquiryId').equals(from).modify({ inquiryId: inquiry.id })
      await db.inquiries.delete(from)
    })
  }
}
